import { get } from 'lodash';
import { type ZodError } from 'zod';

import type Order from '~/types/Order';
import type Rate from '~/types/Rate';

import { isValidatedAddressV2 } from '~/components/AddressValidation/utils';
import { QS_LOCAL_STORAGE_KEY } from '~/constants/local-storage-keys';
import { type OrderSelection } from '~/types/Order';
import { type BatchEntryExtended } from '~/types/OrderBatchEntry';
import { defaultQueryClient } from '~/utils/ShippoQueryClientProvider';

import { hasCustomsDescriptionLengthWarning } from '../../../pages/Orders/All/OrderDetailsPage/RatingErrors/getRateWarnings';
import { isEELPFCRequiredForOrderShipmentAndRate } from '../../eelPfc/utils/eelPfc.util';
import { ORDERS_V2_QUERY_KEY } from '../constants/ordersV2.const';
import {
  type OrderShipmentV2,
  type OrderV2,
  type OrdersSearchParameters,
  type OrdersV2APIParameters,
  type OrdersV2Response,
} from '../types/ordersV2.type';
import { mapSearchParametersToAPIParameters } from './ordersV2Parameters.util';

type ValidationError = {
  code: ZodError['errors'][number]['code'];
  details: {
    invalidPath: string;
    invalidValue: unknown;
    orderObjectId: string;
    orderShipmentId: null | string;
  };
  message: ZodError['errors'][number]['message'];
};

export const mapValidationErrors = (
  errors: ZodError['errors'],
  response: OrdersV2Response,
  parameters: OrdersV2APIParameters,
): ValidationError[] => {
  // Excludes undefined and empty array parameters
  const definedParameters = Object.fromEntries(
    Object.entries(parameters).filter(([_, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      if (value === undefined || value === null) return false;
      return true;
    }),
  );

  return errors.map((error) => {
    const orderObjectId = get(response, ['results', error.path[1], 'object_id'], 'unknown');
    const orderShipmentId =
      error.path[2] === 'order_shipments'
        ? get(
            response,
            ['results', error.path[1], 'order_shipments', error.path[3], 'id'],
            'unknown',
          )
        : null;

    const invalidPath = error.path.reduce<string>((acc, key, index) => {
      if (typeof key === 'number') return `${acc}[${key}]`;
      return `${acc}${index > 0 ? '.' : ''}${key}`;
    }, '');
    const invalidValue = get(response, error.path, 'unknown');

    return {
      code: error.code,
      details: {
        invalidPath,
        invalidValue,
        orderObjectId,
        orderShipmentId,
        parameters: definedParameters,
      },
      message: error.message,
    };
  });
};

const refetchOrdersV2 = (parameters: OrdersV2APIParameters) =>
  defaultQueryClient.refetchQueries({
    exact: true,
    queryKey: [ORDERS_V2_QUERY_KEY, parameters],
  });

export const refetchCurrentOrdersV2Search = () => {
  const searchParameters: OrdersSearchParameters = Object.fromEntries(
    new URLSearchParams(JSON.parse(localStorage.getItem(QS_LOCAL_STORAGE_KEY) || '{}')),
  );
  const apiParameters = mapSearchParametersToAPIParameters(searchParameters);
  if (!apiParameters) return;

  refetchOrdersV2(apiParameters);
};

export const updateOrdersV2 = (updateOrder: (order: OrderV2) => OrderV2) => {
  defaultQueryClient.setQueriesData<OrdersV2Response>([ORDERS_V2_QUERY_KEY], (oldData) => {
    if (!oldData) return;
    return { ...oldData, results: oldData.results.map(updateOrder) };
  });
};

export const removeOrdersV2FromCache = (orderIds: string[]) => {
  const orderIdSet = new Set(orderIds);
  defaultQueryClient.setQueriesData<OrdersV2Response>([ORDERS_V2_QUERY_KEY], (oldData) => {
    if (!oldData) return;
    return { ...oldData, results: oldData.results.filter((o) => !orderIdSet.has(o.object_id)) };
  });
};

/**
 * Returns all orders from all cached OrdersV2 queries, sorted by most recently updated query
 * (most recently updated query first).
 */
export const getAllOrdersV2 = (): OrderV2[] => {
  const queriesData = defaultQueryClient.getQueriesData<OrdersV2Response>([ORDERS_V2_QUERY_KEY]);
  const queriesStates = queriesData
    .map(([key]) => defaultQueryClient.getQueryState<OrdersV2Response>(key))
    .sort((a, b) => (b?.dataUpdatedAt || 0) - (a?.dataUpdatedAt || 0));

  return queriesStates.map((state) => state?.data?.results || []).flat();
};

export const getAllOrderShipmentsV2 = (): OrderShipmentV2[] => {
  const allOrdersV2 = getAllOrdersV2();
  return allOrdersV2.map((order) => order.order_shipments).flat();
};

export const getOrderV2ById = (orderObjectId: string): OrderV2 | undefined => {
  const allOrdersV2 = getAllOrdersV2();
  return allOrdersV2.find((order) => order.object_id === orderObjectId);
};

export const isOrderShipmentRateable = (
  orderShipment:
    | Pick<BatchEntryExtended, 'parcel'>
    | Pick<Order, 'parcel'>
    | Pick<OrderShipmentV2, 'parcel'>,
) => {
  if (!orderShipment.parcel) return false;
  return true;
};

export const isOrderShipmentReadyForPurchase = ({
  eelPfcEnabled,
  order,
  orderShipment,
  selectedRate,
}: {
  eelPfcEnabled: boolean;
  order?: OrderV2;
  orderShipment?: OrderShipmentV2;
  selectedRate?: Rate;
}) => {
  if (!order) return false;
  if (!orderShipment) return false;
  if (!selectedRate) return false;

  if (orderShipment.status.label_status !== 'UNFULFILLED') return false;
  if (orderShipment.status.lifecycle_status !== 'COMPLETE_SHIPMENT') return false;

  const isFulfilledOutside = order.status.fulfillment_status === 'EXTERNALLY_FULFILLED';
  if (isFulfilledOutside) return false;
  if (!isValidatedAddressV2(orderShipment?.address_to, !isFulfilledOutside)) return false;

  if (
    isEELPFCRequiredForOrderShipmentAndRate(
      {
        addressFrom: orderShipment?.address_from,
        addressTo: orderShipment?.address_to,
        customsDeclaration: orderShipment?.customs,
      },
      selectedRate?.provider,
      eelPfcEnabled,
    )
  )
    return false;

  if (hasCustomsDescriptionLengthWarning(orderShipment?.customs_items, selectedRate?.provider))
    return false;

  return true;
};

export const getSelectedOrdersV2WithLabels = (
  selectedOrders: OrderSelection,
  ordersData: OrderV2[],
): OrderSelection =>
  Object.entries(selectedOrders).reduce<OrderSelection>((obj, [orderId, orderShipmentIds]) => {
    const order = ordersData.find((order) => order.object_id === orderId);
    if (!order) return obj;

    if (orderShipmentIds.length > 0) {
      const shipmentsWithTransaction = orderShipmentIds.filter((orderShipmentId) => {
        const shipment = order.order_shipments.find((s) => s.id === orderShipmentId);
        return shipment?.transaction_object_id !== null;
      });

      if (shipmentsWithTransaction.length === 0) return obj;

      return { ...obj, [orderId]: shipmentsWithTransaction };
    }

    const hasTransaction = order.order_shipments.some(
      (shipment) => shipment.transaction_object_id !== null,
    );

    if (hasTransaction) return { ...obj, [orderId]: [] };

    return obj;
  }, {});

export const getSelectedOrdersV2WithSuccessfulTransaction = (
  selectedOrders: OrderSelection,
  ordersData: OrderV2[],
): OrderSelection =>
  Object.entries(selectedOrders).reduce<OrderSelection>((obj, [orderId, orderShipmentIds]) => {
    const order = ordersData.find((order) => order.object_id === orderId);
    if (!order) return obj;

    if (orderShipmentIds.length > 0) {
      const shipmentsWithSuccessfulTransaction = orderShipmentIds.filter((orderShipmentId) => {
        const shipment = order.order_shipments.find((s) => s.id === orderShipmentId);
        return shipment?.transaction?.object_status === 'SUCCESS';
      });

      if (shipmentsWithSuccessfulTransaction.length === 0) return obj;

      return { ...obj, [orderId]: shipmentsWithSuccessfulTransaction };
    }

    const hasSuccessfulTransaction = order.order_shipments.some(
      (shipment) => shipment.transaction?.object_status === 'SUCCESS',
    );

    if (hasSuccessfulTransaction) return { ...obj, [orderId]: [] };

    return obj;
  }, {});

export const getReadyCheckedOrdersV2 = (
  checkedOrders: OrderSelection,
  ordersData: OrderV2[],
  selectedRates: Rate[],
  eelPfcEnabled: boolean,
): OrderSelection =>
  Object.fromEntries(
    Object.entries(checkedOrders)
      .map(([orderId, orderShipmentIds]) => [
        orderId,
        orderShipmentIds.filter((orderShipmentId) => {
          const order = ordersData.find((order) => order.object_id === orderId);

          const orderShipment = order?.order_shipments.find((s) => s.id === orderShipmentId);
          const selectedRate = selectedRates.find(
            (rate) => rate.shipment === orderShipment?.shipment_object_id,
          );

          return isOrderShipmentReadyForPurchase({
            eelPfcEnabled,
            order,
            orderShipment,
            selectedRate,
          });
        }),
      ])
      .filter(([_, orderShipmentIds]) => orderShipmentIds.length > 0),
  ) as OrderSelection;
